import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './BridgeClubPages.css';

interface StoreWithCredits {
  id: number;
  name: string;
  city: string;
  state: string;
  pending_credits: number;
  pending_count: number;
  total_credits: number;
  total_count: number;
}

interface CreditItem {
  id: string;
  store_name: string;
  store_id: number;
  credit_type: string;
  description: string;
  amount: number;
  member_name: string;
  member_code: string;
  status: string;
  created_at: string;
  applied_at: string | null;
  created_by: string;
}

interface CreditsStats {
  total_pending_amount: number;
  total_pending_count: number;
  total_applied_amount: number;
  total_applied_count: number;
}

interface CreditsData {
  stores: StoreWithCredits[];
  recent_credits: CreditItem[];
  stats: CreditsStats;
}

const BridgeClubCredits: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<CreditsData>({
    stores: [],
    recent_credits: [],
    stats: {
      total_pending_amount: 0,
      total_pending_count: 0,
      total_applied_amount: 0,
      total_applied_count: 0,
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'stores' | 'recent'>('stores');
  const [applying, setApplying] = useState<number | null>(null);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<CreditsData>('/admin/bridge-club/store-credits/');
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load credits:', err);
      setError(err.response?.data?.detail || 'Failed to load credits data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const handleApplyCredits = async (storeId: number, storeName: string) => {
    if (!window.confirm(`Mark all pending credits for ${storeName} as applied? This should be done when processing their wholesale order.`)) {
      return;
    }

    setApplying(storeId);
    try {
      const response = await api.post('/admin/bridge-club/credits/apply/', {
        store_id: storeId,
        notes: 'Applied via admin portal'
      });
      alert(response.data.message);
      fetchCredits(); // Refresh data
    } catch (err: any) {
      console.error('Failed to apply credits:', err);
      alert(err.response?.data?.detail || 'Failed to apply credits');
    } finally {
      setApplying(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading credits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bridge-club-page">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club')}>
            &larr; Back to Bridge Club
          </button>
          <h1>Store Credits</h1>
          <p>Credits owed to retail partners for Bridge Club redemptions</p>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchCredits}>Retry</button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="stats-row">
        <div className="stat-card highlight">
          <div className="stat-value">{formatCurrency(data.stats.total_pending_amount)}</div>
          <div className="stat-label">Total Pending Credits</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.stats.total_pending_count}</div>
          <div className="stat-label">Pending Redemptions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(data.stats.total_applied_amount)}</div>
          <div className="stat-label">Total Applied</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.stats.total_applied_count}</div>
          <div className="stat-label">Applied Redemptions</div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="section-tabs">
        <button
          className={`tab-button ${activeSection === 'stores' ? 'active' : ''}`}
          onClick={() => setActiveSection('stores')}
        >
          Stores with Credits ({data.stores.length})
        </button>
        <button
          className={`tab-button ${activeSection === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveSection('recent')}
        >
          Recent Credits ({data.recent_credits.length})
        </button>
      </div>

      {/* Stores with Credits */}
      {activeSection === 'stores' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Stores with Pending Credits</h2>
            <p>Apply credits when processing the store's wholesale order</p>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Location</th>
                  <th>Pending Redemptions</th>
                  <th>Pending Credit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.stores.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      No stores with pending credits
                    </td>
                  </tr>
                ) : (
                  data.stores.filter(s => s.pending_credits > 0).map(store => (
                    <tr key={store.id}>
                      <td>
                        <span className="store-name">{store.name}</span>
                      </td>
                      <td>{store.city}, {store.state}</td>
                      <td className="center">{store.pending_count}</td>
                      <td className="highlight-text">
                        {formatCurrency(store.pending_credits)}
                      </td>
                      <td>
                        <button
                          className="action-btn apply-btn"
                          onClick={() => handleApplyCredits(store.id, store.name)}
                          disabled={applying === store.id}
                        >
                          {applying === store.id ? 'Applying...' : 'Mark Applied'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Credits */}
      {activeSection === 'recent' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Recent Credits</h2>
            <p>Latest credit activity across all stores</p>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Member</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_credits.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      No recent credits
                    </td>
                  </tr>
                ) : (
                  data.recent_credits.map(credit => (
                    <tr key={credit.id}>
                      <td>{credit.store_name}</td>
                      <td>
                        <div className="member-info">
                          <span className="member-name">{credit.member_name}</span>
                          <span className="member-code">{credit.member_code}</span>
                        </div>
                      </td>
                      <td className="highlight-text">{formatCurrency(credit.amount)}</td>
                      <td>
                        <span className={`status-badge ${credit.status}`}>
                          {credit.status === 'pending' ? 'Pending' : 'Applied'}
                        </span>
                      </td>
                      <td>{formatDate(credit.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeClubCredits;
